import { useMapCoordinates } from 'hooks/useMapCoordinates';
import Map from 'components/Map/Map';
import { calculateParkingMetrics } from 'views/ProFormaTable/helpers';
import { IProFormaValues } from 'views/ProFormaTable/types';
import { numeralFormatter } from 'ns_libs/formatter';
import ExecutiveSummaryCard from '../ExecutiveSummaryCard/ExecutiveSummaryCard';
import { useProFormaSocketContext } from '../../ProForma/socketContext/ProFormaSocketProvider';
import { ProFormaModelIds } from 'constants/proForma';
import { isApartmentModel, isStudentHousingModel } from 'helpers/proForma';

const ExecutiveSummaryDetail = () => {
    const { proForma, incomeSummary, buildableLots } = useProFormaSocketContext();

    const isApartmentWithRetail = proForma?.proFormaModelId === ProFormaModelIds.APARTMENT_WITH_RETAIL;

    const totalUnits = incomeSummary?.totalUnits || 0;
    const totalBeds = incomeSummary?.totalBeds || 0;
    const totalRentableSf = incomeSummary?.totalRentableSf || 0;

    const buildings = proForma?.buildings || 0;
    const stories = proForma?.averageStories || 0;
    const address = [
        proForma?.address,
        proForma?.city,
        proForma?.state,
        proForma?.zipCode,
    ].filter(Boolean).join(', ');
    const description = proForma?.description || '';
    const retailBuiltGrossSquareFootage = proForma?.retailBuiltGrossSquareFootage ? Number(proForma.retailBuiltGrossSquareFootage) : 0;
    const grossBuiltSf = proForma?.builtGrossSquareFootage ? Number(proForma.builtGrossSquareFootage) : 0;
    const totalGrossBuiltSf = isApartmentWithRetail ? grossBuiltSf + retailBuiltGrossSquareFootage : grossBuiltSf;
    const efficiencyRatio = totalRentableSf && totalGrossBuiltSf
        ? numeralFormatter(((totalRentableSf / totalGrossBuiltSf) * 100), '0,0.00')
        : undefined;

    const proFormaValues: IProFormaValues = {
        ...proForma,
        landArea: buildableLots.totalLandAreaSquareFootage || null,
        floorAreaRatio: buildableLots.totalFar || null,
        grossBuildableArea: buildableLots.totalGrossBuildableArea || null,
        builtGrossSquareFootage: proForma?.builtGrossSquareFootage ? Number(proForma.builtGrossSquareFootage) : null,
        efficiencyRatio: proForma?.efficiencyRatio != null ? Number(proForma.efficiencyRatio) * 100 : null,
        estimatedUnits: proForma?.estimatedUnits != null ? Number(proForma.estimatedUnits) : null,
        estimatedRacks: proForma?.estimatedRacks != null ? Number(proForma.estimatedRacks) : null,
        retailBuiltGrossSquareFootage: proForma?.retailBuiltGrossSquareFootage != null
            ? Number(proForma.retailBuiltGrossSquareFootage) : null,
        retailEfficiencyRatio: proForma?.retailEfficiencyRatio != null ? Number(proForma.retailEfficiencyRatio) * 100 : null,
    };

    const {
        totalSpots: parkingSpaces, ratio: parkingRatio, suffix: parkingSuffix,
    } = calculateParkingMetrics(proFormaValues, totalUnits, proForma.proFormaModelId);

    const { lat, lng } = useMapCoordinates(proForma ? {
        address: proForma.address ?? undefined,
        city: proForma.city ?? undefined,
        state: proForma.state ?? undefined,
        zipCode: proForma.zipCode ?? undefined,
        latitude: proForma.latitude ?? undefined,
        longitude: proForma.longitude ?? undefined,
    } : null);

    const detailSections = [
        {
            id: 'details',
            title: 'Details',
            items: [
                { id: 'address', label: 'Address', value: address },
                {
                    id: 'landArea',
                    label: 'Land area',
                    value: `${numeralFormatter(buildableLots.totalLandAreaSquareFootage, '0,0.[00]')} SF`,
                },
                { id: 'far', label: 'FAR', value: buildableLots.totalFar ? numeralFormatter(buildableLots.totalFar, '0,0.00') : '—' },
                {
                    id: 'grossBuildableArea',
                    label: 'Gross buildable area',
                    value: `${numeralFormatter(buildableLots.totalGrossBuildableArea, '0,0.[00]')} SF`,
                },
                { id: 'buildings', label: 'Buildings', value: numeralFormatter(buildings) },
                { id: 'stories', label: 'Stories', value: numeralFormatter(stories) },
                ...(isApartmentModel(proForma.proFormaModelId) || isStudentHousingModel(proForma.proFormaModelId)
                    ? [{ id: 'units', label: 'Units', value: numeralFormatter(totalUnits) }]
                    : []),
                ...(isStudentHousingModel(proForma.proFormaModelId)
                    ? [{ id: 'beds', label: 'Beds', value: numeralFormatter(totalBeds) }]
                    : []),
                { id: 'grossBuiltSf', label: 'Gross Built SF', value: `${numeralFormatter(totalGrossBuiltSf)} GSF` },
                { id: 'netRentableArea', label: 'Net Rentable Area', value: `${numeralFormatter(totalRentableSf)} RSF` },
                { id: 'efficiencyRatio', label: 'Building efficiency ratio', value: efficiencyRatio ? `${efficiencyRatio}%` : '—' },
                { id: 'parkingSpaces', label: 'Parking spaces', value: numeralFormatter(parkingSpaces) },
                { id: 'parkingRatio', label: 'Parking ratio', value: parkingRatio ? `${parkingRatio} ${parkingSuffix}` : 'N/A' },
            ],
        },
    ];

    if (description && description.trim()) {
        detailSections.push({
            id: 'description',
            title: 'Description',
            items: [
                {
                    id: 'description',
                    value: description,
                    label: 'Description',
                },
            ],
        });
    }

    return (
        <div className="d-flex justify-content-between align-items-stretch mb-3">
            <ExecutiveSummaryCard containerClassName="w-50 mr-1" sections={detailSections} />
            <div className="ml-1 w-50 d-flex position-relative">
                <Map lat={lat} lng={lng} height="100%" />
            </div>
        </div>
    );
};

export default ExecutiveSummaryDetail;
